




import {Component, Prop, Vue} from "vue-property-decorator";
import PxDropdown from "@/views/components/input/formFields/PxDropdown.vue";
import PxNumberInput from "@/views/components/input/formFields/PxNumberInput.vue";
import ProbabilisticChart from "@/modules/lcca/views/components/ProbablisticChart.vue";
import PxTextArea from "@/views/components/input/formFields/PxTextArea.vue";
import PxTextEntryBox from "@/views/components/input/formFields/PxTextEntryBox.vue";
import QuickHelp from "@/views/components/input/QuickHelp.vue";
import PrVehicleDistributionPlot from "@/modules/perRoad/views/components/traffic/PrVehicleDistributionPlot.vue";
import {
  IPrSimulatedLayerImperial,
  IPrSimulatedLayerMetric,
  IWesleaConfig
} from "@/modules/perRoad/interfaces/monteCarlo";

// const echarts = require('echarts');
import * as echarts from "echarts";
import tinygradient from "tinygradient";
import {UnitSystem} from "@/interfaces/units";

@Component({
  components: {
    PrVehicleDistributionPlot,
    QuickHelp,
    PxTextEntryBox,
    PxTextArea,
    ProbabilisticChart,
    PxDropdown,
    PxNumberInput,
  },
})
export default class CriticalLocationDepthInspector extends Vue {
  @Prop() id!: string;

  @Prop() layers!: IPrSimulatedLayerImperial[] | IPrSimulatedLayerMetric[];

  @Prop({default: null}) wesleaConfig!: IWesleaConfig | null;

  @Prop({default: 0}) externalPlotKey!: number;

  // @Prop() wesleaResults!: IWesleaResult[];

  get plotId() {
    return `id-${this.id}-critical-depth`;
  }

  internalPlotKey = 0;

  get plotKey() {
    return this.internalPlotKey + this.externalPlotKey;
  }

  updatePlotKey() {
    this.internalPlotKey += 1;
  }

  chart: any = null;

  /**
   * Chart formatting
   */

  get chartXAxisLimits() {
    /**
     * Calculate content-aware limits for x axis
     */
    let xMin = 0;
    let xMax = 0;
    if (this.wesleaConfig && this.wesleaConfig.criticallocations.length > 0) {
      xMax = Math.max(...this.criticalLocationSeries[0].data.map((c: number[]) => c[0]));
    }

    // Center on zero for single axle
    if (xMin === xMax) {
      xMin = -10;
      xMax = 10;
    }

    // Pad left and right of chart by amt
    const padding = 0.8;
    const width = xMax - xMin;
    const minPad = -(width * padding) / 2;
    const maxPad = (width * padding) / 2;

    return [xMin + minPad, xMax + maxPad];
  }

  get chartYAxisLimits() {

    /**
     * Dynamically set Y axis based on layer thickness: limit to reasonable values
     * */
        // @ts-ignore implicit any on layer due to type signature mismatch, it's ok i swear!!
    const depths = this.layers.map(layer => {
          if (layer.unitSystem === UnitSystem.Imperial) {
            if (layer.layerNumber === 5 || layer.thickness_in > 100) {
              // Don't use depth for bottom layer, it's super deep, effectively infinite
              return 0;
            }
            return layer.depthOfLayerBottom_in;
          } else {
            if (layer.layerNumber === 5 || layer.thickness_cm > 500) {
              // Don't use depth for bottom layer, it's super deep, effectively infinite
              return 0;
            }
            return layer.depthOfLayerBottom_cm;
          }
        });

    console.log(depths);

    let yMin = -Math.max(...depths);

    const yMax = 0;

    // Pad left and right of chart by amt
    const padding = .2;
    const width = yMax - yMin;
    const minPad = -(width * padding) / 2;
    const maxPad = (width * padding) / 2;

    console.log(yMin, yMax);

    return [yMin + minPad, yMax + maxPad];

  }

  /**
   * Data series
   */

  get layerLineSeries(): any[] {
    /**
     * Plots the lines demarcating the boundary between each layer.
     */

    const series: any[] = [];

    // @ts-ignore implicit any on layer due to type signature mismatch, it's ok i swear!!
    this.layers.forEach((layer, i) => {

      const depthOfLayerTop = layer.unitSystem === UnitSystem.Imperial ? layer.depthOfLayerTop_in : layer.depthOfLayerTop_cm;

      series.push({
        type: "line",
        lineStyle: {
          color: "black"
        },
        symbol: "none",
        z: 10,
        name: `Layer ${i} Top`,
        endLabel: {
          show: true,
          formatter: "{a} {b} {c}",
        },
        data: [
          [this.chartXAxisLimits[0], -depthOfLayerTop],
          [this.chartXAxisLimits[1], -depthOfLayerTop]
        ],
        animation: false,
      });
    });
    return series;
  }

  get layerRectangleSeries() {
    /**
     * Creates the data series for showing the layers of pavement. Uses custom rendering.
     */

    const series: any[] = [];
    // @ts-ignore ts not liking the type signature for layers -> Implicit any
    const layerData = this.layers.map(layer => {

      const depthOfLayerTop = layer.unitSystem === UnitSystem.Imperial ? layer.depthOfLayerTop_in : layer.depthOfLayerTop_cm;
      const depthOfLayerBottom = layer.unitSystem === UnitSystem.Imperial ? layer.depthOfLayerBottom_in : layer.depthOfLayerBottom_cm;

      const modulus = layer.unitSystem === UnitSystem.Imperial ? layer.modulus_psi : layer.modulus_mpa;

      return {
        value: [-depthOfLayerTop, -depthOfLayerBottom, modulus, layer.id],
        layer: layer
      };
    });

    series.push({
      type: "custom",
      name: "Layer",
      data: layerData,
      z: 5,
      tooltip: {
        formatter: (params: any, ticket: any, callback: any) => {

          const modulus = params.data.value[2];
          const modulusUnitsStr = params.data.layer.unitSystem === UnitSystem.Imperial ? "psi" : "MPa";

          return `<strong>${params.data.layer.name}</strong><br>E = ${(modulus).toLocaleString()} ${modulusUnitsStr}<br>𝜈 = ${params.data.layer.poissonRatio}`;
        }
      },
      renderItem: (params: any, api: any) => {

        //@ts-ignore: layer has implicit 'any' type because the two signatures aren't compatible
        const layer = this.layers.filter(layer => layer.id === api.value(3))[0];

        const start = api.coord([this.chartXAxisLimits[0], api.value(0)]);

        let lowestYValue = api.value(1);
        if (this.chartYAxisLimits[0] > lowestYValue) {
          lowestYValue = this.chartYAxisLimits[0];
        }

        const size = api.size([this.chartXAxisLimits[1] - this.chartXAxisLimits[0], lowestYValue - api.value(0)]);


        // Gradient by log of modulus
        const gradientMinPos = 0;
        // const gradientMaxPos = Math.max(...this.draw.layers.map(layer => Math.log(layer.modulus_psi)))
        const maxPsi = 2000000;
        const maxGradient = layer.unitSystem === UnitSystem.Imperial ? maxPsi : 0.00689476 * maxPsi;
        const gradientMaxPos = Math.log(maxGradient);

        const gradient = tinygradient([
          {color: "#FFFFFF", pos: 0},
          {color: "#333333", pos: 1}
        ]);

        const fillColor = gradient.rgbAt(Math.log(api.value(2)) / gradientMaxPos);

        const thicknessValue = layer.unitSystem === UnitSystem.Imperial ? layer.thickness_in : layer.thickness_cm;
        const thicknessUnit = layer.unitSystem === UnitSystem.Imperial ? `inches` : `cm`;
        const thicknessText = `${thicknessValue.toFixed(2)} ${thicknessUnit}`;

        return {
          type: "rect",
          shape: {
            x: start[0],
            y: start[1],
            width: size[0],
            height: size[1]
          },
          style: {
            fill: fillColor,
          },
          textContent: {
            type: "text",
            style: {
              text: `${layer.name}: ${thicknessText}`,
              fill: "#000",
              background: "#FFF",
              shadowColor: "#FFF",
              shadowOffsetX: 10
            }
          },
          textConfig: {
            position: "insideTopLeft",
          },
          z: 6,
        };
      }
    });

    return series;
  }

  get criticalLocationSeries(): any[] {

    if (!this.wesleaConfig || this.wesleaConfig.criticallocations.length === 0) {
      return [];
    }

    const series: any [] = [{
      type: "scatter",
      name: "Critical Location",
      symbol: "diamond",
      symbolSize: 20,
      itemStyle: {
        color: "red"
      },
      z: 20,
      data: [],
      tooltip: {
        formatter: (params: any, ticket: any, callback: any) => {
          const critLocId = params.data[2];
          if (this.wesleaConfig) {
            const cr = this.wesleaConfig.criticallocations[critLocId];
            return `<strong>${params.seriesName} ${critLocId}-${cr.l}</strong>`;
          }
          return "";
        }
      },
    }];

    this.wesleaConfig?.criticallocations.forEach((c, i) => {
      // series[0].data.push({value: [c.y, -c.z], name: `${i}-${c.l}`})
      series[0].data.push([c.y, -c.z, i, c.l]);
    });
    return series;
  }

  /**
   *
   * Chart creation, updating
   *
   */

  createPlot() {
    const el = document.getElementById(this.plotId);
    //@ts-ignore
    this.chart = echarts.init(el);

    const lengthUnitsStr = this.layers[0].unitSystem === UnitSystem.Imperial ? "in" : "cm";
    console.log(lengthUnitsStr, this.layers[0]);

    this.chart.setOption({
      grid: {
        top: 5,
        right: 5,
        bottom: 45,
        left: 45,
        show: true,
        borderWidth: 1,
        borderColor: "#000",
      },
      xAxis: {
        name: `Longitudinal Distance (${lengthUnitsStr})`,
        nameLocation: "center",
        nameGap: 25,
        min: this.chartXAxisLimits[0],
        max: this.chartXAxisLimits[1],
        splitLine: {
          show: false
        },
        z: 100
      },
      yAxis: {
        name: `Depth (${lengthUnitsStr})`,
        nameLocation: "center",
        nameGap: 25,
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        splitLine: {
          show: false
        },
        axisLabel: {
          showMinLabel: false,
          showMaxLabel: false,
        },
        minInterval: 1,
        min: this.chartYAxisLimits[0],
        max: this.chartYAxisLimits[1],
        z: 100
      },

      tooltip: {
        trigger: "item"
      },
      series: [
        ...this.layerLineSeries,
        ...this.criticalLocationSeries,
        ...this.layerRectangleSeries
      ]
    });
  }

  updated() {
    this.createPlot();
  }

  mounted() {
    console.log("layers", this.layerRectangleSeries);
    this.createPlot();
    window.onresize = () => {
      if (this.chart !== null && this.chart !== undefined) {
        this.chart.resize();
      }
    };
  }

}
